import { ColorBadge, RadioButtonCard, SimpleSparkleIcon, TruckIcon } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React from 'react';
import { createUseStyles } from 'react-jss';
import TRUCKLANE_MINIMUM_MODE from '../../../enums/trucklane-minimum-mode';
import StepContainer from '../components/step-container';
import InputCard from '../../../components/inputs/input-card';
import ToggleBar from '../../../components/inputs/toggle-bar';
import { labels } from './utils';

const useStyles = createUseStyles({
  cardWrapper: {
    position: 'relative',
  },
  lineBreak: {
    padding: 0,
    margin: 0,
  },
  description: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    letterSpacing: '-0.084px',
  },
  italicBold: {
    fontStyle: 'italic',
    fontWeight: 500,
  },
  stepContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
});

const modeOptions = [
  {
    label: labels[TRUCKLANE_MINIMUM_MODE.TOTAL_REVENUE],
    value: TRUCKLANE_MINIMUM_MODE.TOTAL_REVENUE,
    description: (
      <span>
        Your priority is gross revenue. Truck lanes will be suggested based on generating the most total revenue. Delivery costs will not be
        considered.
      </span>
    ),
  },
  {
    label: labels[TRUCKLANE_MINIMUM_MODE.NET_REVENUE],
    value: TRUCKLANE_MINIMUM_MODE.NET_REVENUE,
    description: (
      <span>
        Balancing revenue and freight cost is your priority. Truck lanes will be suggested only when total revenue is greater than delivery costs.
      </span>
    ),
  },
];

const SmartAwardSettingsSteps = ({
  userMinimums,
  setTouched,
  setMinimum,
  setMode,
  useWeightMinimum,
  usePalletMinimum,
  setUseWeightMinimum,
  setUsePalletMinimum,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.stepContainer}>
      <StepContainer
        icon={() => <SimpleSparkleIcon style={{ width: 32, height: 24 }} />}
        stepNumber={1}
        title="Optimize smart awarding"
        description="Choose an option below that aligns most with your awarding priorities so that our algorithm can find the best awarding suggestions for you."
      >
        <div className={classes.cardWrapper}>
          <RadioButtonCard onChange={setMode} options={modeOptions} selected={userMinimums.mode} />
        </div>
      </StepContainer>
      <StepContainer
        icon={() => <TruckIcon style={{ maxWidth: 24 }} />}
        stepNumber={2}
        title="Truck lane minimums (optional)"
        description={
          <span className={classes.description}>
            Select any combination of parameters below to set your truck lane minimums. If more than one is selected,{' '}
            <span className={classes.italicBold}>all chosen minimums must be met for a truck lane to be suggested.</span> Smart Awarding will optimize
            for your selected revenue type in step 1 while ensuring the minimums you set here are respected.
          </span>
        }
      >
        <ToggleBar
          useWeightMinimum={useWeightMinimum}
          setUseWeightMinimum={setUseWeightMinimum}
          usePalletMinimum={usePalletMinimum}
          setUsePalletMinimum={setUsePalletMinimum}
          setTouched={setTouched}
          mode={userMinimums.mode}
        />
        <hr className={classes.lineBreak} />
        {true && (
          <InputCard
            title={
              userMinimums.mode === TRUCKLANE_MINIMUM_MODE.TOTAL_REVENUE
                ? labels[TRUCKLANE_MINIMUM_MODE.TOTAL_REVENUE]
                : labels[TRUCKLANE_MINIMUM_MODE.NET_REVENUE]
            }
            pickupMinimum={'pickupRevenue'}
            deliveryMinimum={'deliveryRevenue'}
            userMinimums={userMinimums}
            setTouched={setTouched}
            setMinimum={setMinimum}
          />
        )}
        {useWeightMinimum && <ColorBadge theme={'grey'}>AND</ColorBadge>}
        {useWeightMinimum && (
          <InputCard
            title="Weight"
            pickupMinimum={'pickupWeight'}
            deliveryMinimum={'deliveryWeight'}
            userMinimums={userMinimums}
            setTouched={setTouched}
            setMinimum={setMinimum}
          />
        )}
        {usePalletMinimum && <ColorBadge theme={'grey'}>AND</ColorBadge>}
        {usePalletMinimum && (
          <InputCard
            title="Pallets"
            pickupMinimum={'pickupPallets'}
            deliveryMinimum={'deliveryPallets'}
            userMinimums={userMinimums}
            setTouched={setTouched}
            setMinimum={setMinimum}
          />
        )}
      </StepContainer>
    </div>
  );
};

export default SmartAwardSettingsSteps;

const TruckTypeMinimums = PropTypes.shape({
  dry: PropTypes.number,
  refrigerated: PropTypes.number,
  frozen: PropTypes.number,
});

SmartAwardSettingsSteps.propTypes = {
  userMinimums: PropTypes.shape({
    mode: PropTypes.string,
    deliveryRevenue: TruckTypeMinimums,
    pickupRevenue: TruckTypeMinimums,
    deliveryWeight: TruckTypeMinimums,
    pickupWeight: TruckTypeMinimums,
    deliveryPallets: TruckTypeMinimums,
    pickupPallets: TruckTypeMinimums,
  }),
  setMinimum: PropTypes.func,
  setTouched: PropTypes.func,
  setMode: PropTypes.func,
  useWeightMinimum: PropTypes.bool,
  usePalletMinimum: PropTypes.bool,
  setUseWeightMinimum: PropTypes.func,
  setUsePalletMinimum: PropTypes.func,
};
